import type React from "react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/ui/PaywallButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { WizardLayout } from "@/components/layout/wizard-layout";
import {
  CheckCircle2,
  Building,
  TrendingUp,
  Users,
  BarChart3,
  AlertTriangle,
  Info,
} from "lucide-react";
import { formatCurrency, formatDualCurrency } from "@/utils/currency-utils";
import { POSTHOG_EVENTS, SERVICE_MAPPING_STEPS } from "../../constants";
import type { EntityData } from "@/types/entity";
import { usePostHog } from "posthog-js/react";
import { evaluateEntitySize } from "../../utils/size";
import type { SizeEvaluation } from "../../types";
import { useToast } from "@/hooks/use-toast";
import { useGetBilant } from "@/hooks/api/query/useGetBilant";
import { AnBilantSelector } from "@/components/onboarding/shared/AnBilantSelector";
import { buildSurseDate } from "@/utils/entitySurse";
import { etichetaBilant } from "@/utils/surseDateFormatting";
import type { AnBilant, SurseDate } from "@/types/identificareFirma";

type FinancialFieldErrors = {
  nrSalariati?: string;
  cifraAfaceri?: string;
  active?: string;
};

function hasValidFinancialInputs(data: {
  nrSalariati: number;
  cifraAfaceri: number;
  active: number;
}): boolean {
  return data.nrSalariati > 0 && (data.cifraAfaceri > 0 || data.active > 0);
}

function buildFinancialFieldErrors(data: {
  nrSalariati: number;
  cifraAfaceri: number;
  active: number;
}): FinancialFieldErrors {
  const errors: FinancialFieldErrors = {};
  if (data.nrSalariati <= 0) {
    errors.nrSalariati = "Introduceți numărul de salariați";
  }
  if (data.cifraAfaceri <= 0 && data.active <= 0) {
    errors.cifraAfaceri =
      "Completați cel puțin una dintre cifra de afaceri sau active";
    errors.active =
      "Completați cel puțin una dintre cifra de afaceri sau active";
  }
  return errors;
}

const STEPS = [
  "Identificare",
  "Dimensiune",
  "Servicii NIS 2",
  "Esențiale",
  "Importante",
  "Rezultate",
  "Generare Raport",
];

type EntitySize = "MICĂ" | "MIJLOCIE" | "MARE";

interface Step3SizeEvaluationProps {
  entityData: EntityData;
  onNext: (
    size: EntitySize,
    financialData: {
      nrSalariati: number;
      cifraAfaceri: number;
      active: number;
      an?: number | null;
      cifraAfaceriRon?: number;
      activeRon?: number;
      curs?: number | null;
      surseDate?: SurseDate | null;
    }
  ) => void;
  onBack: () => void;
  onStepClick?: (stepIndex: number) => void;
  currentStep?: number;
  steps?: string[];
  userAvatar?: React.ReactNode;
  isLoading?: boolean;
  sessionId?: string;
}

export function Step3SizeEvaluation({
  entityData,
  onNext,
  onBack,
  onStepClick,
  currentStep = 1,
  steps = SERVICE_MAPPING_STEPS,
  userAvatar,
  isLoading = false,
  sessionId,
}: Step3SizeEvaluationProps) {
  const posthog = usePostHog();
  const { toast } = useToast();

  const hasInsufficientCredits = false;

  const [isEditMode, setIsEditMode] = useState(false);
  const [financialData, setFinancialData] = useState({
    nrSalariati: entityData?.nr_salariati || 0,
    cifraAfaceri: entityData?.cifra_afaceri || 0,
    active: entityData?.active || 0,
  });
  const [sizeEvaluation, setSizeEvaluation] = useState<SizeEvaluation | null>(
    null
  );
  const [fieldErrors, setFieldErrors] = useState<FinancialFieldErrors>({});
  const [anSelectat, setAnSelectat] = useState<number | null>(
    entityData?.financial_data_year ?? null
  );
  const { data: bilant } = useGetBilant(entityData?.cui);
  const aniBilant: AnBilant[] = bilant?.ani ?? [];
  const anBilantCurent =
    aniBilant.find((element) => element.an === anSelectat) ?? null;
  const cifraAfaceriRon =
    anBilantCurent?.cifra_afaceri ??
    entityData?.original_cifra_afaceri_ron ??
    0;
  const activeRon =
    anBilantCurent?.active_totale ?? entityData?.original_active_ron ?? 0;
  const surseBilant: SurseDate["bilant"] = anBilantCurent
    ? {
        an: anBilantCurent.an,
        sursa: anBilantCurent.sursa,
        publicat: anBilantCurent.publicat_la,
      }
    : (entityData?.surse_date?.bilant ?? null);

  const { state } = useSidebar();
  const sidebarWidth = state === "collapsed" ? 60 : 250;
  const isMobile = useIsMobile();

  const isLoadingCredits = isLoading;

  const hasFinancialInputs = hasValidFinancialInputs(financialData);

  useEffect(() => {
    const nextFinancialData = {
      nrSalariati: entityData?.nr_salariati || 0,
      cifraAfaceri: entityData?.cifra_afaceri || 0,
      active: entityData?.active || 0,
    };
    setFinancialData(nextFinancialData);

    if (hasValidFinancialInputs(nextFinancialData)) {
      const evaluation = evaluateEntitySize(
        nextFinancialData.nrSalariati,
        nextFinancialData.cifraAfaceri,
        nextFinancialData.active
      );
      setSizeEvaluation(evaluation);
    } else {
      setSizeEvaluation(null);
      setIsEditMode(true);
    }
  }, [entityData]);

  const handleDataChange = (
    field: keyof typeof financialData,
    value: number
  ) => {
    const newFinancialData = { ...financialData, [field]: value };

    if (hasValidFinancialInputs(newFinancialData)) {
      const evaluation = evaluateEntitySize(
        newFinancialData.nrSalariati,
        newFinancialData.cifraAfaceri,
        newFinancialData.active
      );
      setSizeEvaluation(evaluation);
    } else {
      setSizeEvaluation(null);
    }

    setFinancialData(newFinancialData);
    setFieldErrors((prev) => {
      if (!prev[field] && !(field === "cifraAfaceri" || field === "active")) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      if (field === "cifraAfaceri" || field === "active") {
        if (newFinancialData.cifraAfaceri > 0 || newFinancialData.active > 0) {
          delete next.cifraAfaceri;
          delete next.active;
        }
      }
      return next;
    });
  };

  const handleAnChange = (an: number) => {
    const ales = aniBilant.find((element) => element.an === an);
    if (!ales) return;
    setAnSelectat(an);
    const curs = ales.curs?.curs ?? null;
    const nextFinancialData = {
      nrSalariati: ales.salariati ?? 0,
      cifraAfaceri: curs
        ? Math.round(((ales.cifra_afaceri ?? 0) / curs) * 100) / 100
        : 0,
      active: curs
        ? Math.round(((ales.active_totale ?? 0) / curs) * 100) / 100
        : 0,
    };
    setFinancialData(nextFinancialData);
    setSizeEvaluation(
      hasValidFinancialInputs(nextFinancialData)
        ? evaluateEntitySize(
            nextFinancialData.nrSalariati,
            nextFinancialData.cifraAfaceri,
            nextFinancialData.active
          )
        : null
    );
  };

  const handleContinue = () => {
    if (!hasFinancialInputs || !sizeEvaluation) {
      setFieldErrors(buildFinancialFieldErrors(financialData));
      setIsEditMode(true);
      toast({
        title: "Date financiare incomplete",
        description: "Completați datele financiare înainte de a continua.",
        variant: "destructive",
      });
      return;
    }
    setFieldErrors({});
    onNext(sizeEvaluation.size, {
      ...financialData,
      an: anSelectat,
      cifraAfaceriRon: anBilantCurent?.cifra_afaceri ?? undefined,
      activeRon: anBilantCurent?.active_totale ?? undefined,
      curs: anBilantCurent?.curs?.curs ?? null,
      surseDate: bilant
        ? buildSurseDate({
            sursa: entityData?.surse_date?.identificare?.sursa,
            la: entityData?.surse_date?.identificare?.la,
            snapshot: entityData?.surse_date?.identificare?.snapshot,
            bilant: { ...bilant, an_recomandat: anSelectat },
          })
        : (entityData?.surse_date ?? null),
    });
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("ro-RO").format(num);
  };

  const getSizeBadgeVariant = (size: EntitySize) => {
    switch (size) {
      case "MICĂ":
        return "secondary";
      case "MIJLOCIE":
        return "default";
      case "MARE":
        return "destructive";
    }
  };

  if (!isEditMode && sizeEvaluation) {
    return (
      <WizardLayout
        title="Evaluare Dimensiune Entitate"
        description={`Datele financiare ${surseBilant?.an ? `provin din ${etichetaBilant(surseBilant)}` : "trebuie introduse manual"}. Vă rugăm să le evaluați.`}
        currentStep={currentStep}
        steps={steps}
        currentSubSteps={[
          {
            id: "3.A",
            label: "Confirmare Date Dimensiune Entitate",
            status: "completed",
          },
          {
            id: "3.B",
            label: "Validare Date Dimensiune Entitate",
            status: "current",
          },
          {
            id: "3.C",
            label: "Rezultat Evaluare",
            status: "upcoming",
          },
        ]}
        onBack={onBack}
        onStepClick={onStepClick}
        showBackButton
        userAvatar={userAvatar}
        cardClassName="mb-[250px]"
      >
        <div className="space-y-4 md:space-y-6">
          {/* Automatic evaluation result */}
          <Card className="border-2 border-primary/20 bg-primary/5">
            <CardHeader>
              <div
                className={
                  isMobile ? "space-y-4" : "flex items-center justify-between"
                }
              >
                <div className="flex items-center gap-3">
                  <div className="min-w-10 w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Building className="w-4 h-4 md:w-6 md:h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base md:text-xl">
                      Rezultat Evaluare Automată
                    </CardTitle>
                    <CardDescription>
                      Dimensiunea entității determinată conform legislației
                      {surseBilant?.an
                        ? ` · ${etichetaBilant(surseBilant)}`
                        : ""}
                    </CardDescription>
                  </div>
                </div>
                <div className={isMobile ? "flex justify-center" : ""}>
                  <Badge
                    variant={getSizeBadgeVariant(sizeEvaluation.size)}
                    className="text-base md:text-lg px-3 py-1 md:px-4 md:py-2 font-bold"
                  >
                    ENTITATE {sizeEvaluation.size}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-6 md:pt-0 md:pb-6">
              {aniBilant.length > 0 && (
                <div className="mb-4">
                  <AnBilantSelector
                    ani={aniBilant}
                    anSelectat={anSelectat}
                    sursa={surseBilant}
                    onChange={handleAnChange}
                  />
                </div>
              )}
              <p className="text-muted-foreground mb-4 text-xs md:text-sm">
                {sizeEvaluation.explanation}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                <div className="flex items-center gap-3 p-3 md:p-4 bg-white rounded-lg border">
                  <Users className="w-5 h-5 text-ds-blue-600" />
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      Numărul de angajați
                    </p>
                    <p className="font-semibold">
                      {formatNumber(financialData.nrSalariati)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 md:p-4 bg-white rounded-lg border">
                  <TrendingUp className="w-5 h-5 text-ds-green-600" />
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      Cifra de afaceri netă
                    </p>
                    <p className="font-semibold">
                      {formatCurrency(financialData.cifraAfaceri, "EUR")}
                    </p>
                    {cifraAfaceriRon > 0 && (
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {" "}
                        {formatCurrency(cifraAfaceriRon, "RON")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 md:p-4 bg-white rounded-lg border">
                  <BarChart3 className="w-5 h-5 text-ds-purple-600" />
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      Active totale
                    </p>
                    <p className="font-semibold">
                      {formatCurrency(financialData.active, "EUR")}
                    </p>
                    {activeRon > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {" "}
                        {formatCurrency(activeRon, "RON")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Criteria breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">
                Analiza Criteriilor de Dimensiune
              </CardTitle>
              <CardDescription className="text-xs md:text-sm">
                Conform Legii 346/2004 și Regulamentului UE 651/2014
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 pb-6 md:pt-0 md:pb-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div
                    className={`p-4 rounded-lg ${sizeEvaluation.size === "MICĂ" ? "border-2 border-ds-blue-600 bg-ds-blue-50" : "border"}`}
                  >
                    <h4 className="font-heading font-semibold text-sm mb-2">
                      MICĂ
                    </h4>
                    <div className="space-y-1 text-xs md:text-sm text-muted-foreground">
                      <p>&lt; 50 angajați</p>
                      <p>&lt; 10 mil EUR (cifra afaceri)</p>
                      <p>&lt; 10 mil EUR (active)</p>
                    </div>
                  </div>

                  <div
                    className={`p-4 rounded-lg ${sizeEvaluation.size === "MIJLOCIE" ? "border-2 border-ds-blue-600 bg-ds-blue-50" : "border"}`}
                  >
                    <h4 className="font-heading font-semibold text-sm mb-2">
                      MIJLOCIE
                    </h4>
                    <div className="space-y-1 text-xs md:text-sm text-muted-foreground">
                      <p>&lt; 250 angajați</p>
                      <p>&lt; 50 mil EUR (cifra afaceri)</p>
                      <p>&lt; 43 mil EUR (active)</p>
                    </div>
                  </div>

                  <div
                    className={`p-4 rounded-lg ${sizeEvaluation.size === "MARE" ? "border-2 border-ds-blue-600 bg-ds-blue-50" : "border"}`}
                  >
                    <h4 className="font-heading font-semibold text-sm mb-2">
                      MARE
                    </h4>
                    <div className="space-y-1 text-xs md:text-sm text-muted-foreground">
                      <p>≥ 250 angajați</p>
                      <p>≥ 50 mil EUR (cifra afaceri)</p>
                      <p>≥ 43 mil EUR (active)</p>
                    </div>
                  </div>
                </div>

                <div className="text-xs md:text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  <strong>Notă:</strong> Pentru clasificare se folosesc
                  criteriile pentru ultimul exercițiu financiar încheiat. Este
                  suficient ca entitatea să îndeplinească criteriul de personal
                  ȘI unul dintre criteriile financiare.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Validation question */}
          <Card
            className={`bg-ds-blue-50 border-ds-blue-200 fixed shadow-lg ${
              isMobile ? "bottom-4 left-4 right-4" : "bottom-10 min-w-[550px]"
            }`}
            style={
              !isMobile
                ? {
                    left: "50%",
                    transform: `translateX(calc(-50% + ${sidebarWidth / 2}px))`,
                  }
                : undefined
            }
          >
            <CardContent className="py-4 md:py-6">
              <div className="text-center">
                <h3 className="font-heading font-semibold md:mb-1 text-base md:text-lg">
                  Confirmați că aceste date sunt corecte și valide?
                </h3>
                <p className="text-muted-foreground mb-3 md:mb-4 text-xs md:text-sm">
                  Dimensiunea entității este esențială pentru determinarea
                  aplicabilității NIS 2
                </p>

                <div className="flex flex-col sm:flex-row gap-2 md:gap-4 justify-center">
                  <PaywallButton
                    locked={!isLoadingCredits && hasInsufficientCredits}
                    leftIcon={
                      isLoadingCredits ? undefined : (
                        <CheckCircle2 className="w-5 h-5" />
                      )
                    }
                    size="sm"
                    variant="hero"
                    onClick={() => {
                      handleContinue();
                      posthog.capture(POSTHOG_EVENTS.STEP_2_CONFIRM_DATA);
                    }}
                    className="min-w-48 w-full md:w-auto text-xs md:text-sm"
                    disabled={isLoadingCredits}
                  >
                    {isLoadingCredits ? (
                      <div className="h-5 w-5 border-2 border-background border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Da, Datele sunt Corecte"
                    )}
                  </PaywallButton>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsEditMode(true);
                      posthog.capture(POSTHOG_EVENTS.STEP_2_EDIT_DATA);
                    }}
                    className="min-w-48 w-full md:w-auto text-xs md:text-sm"
                  >
                    Nu, Doresc să le Editez
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </WizardLayout>
    );
  }

  // Edit mode
  return (
    <WizardLayout
      title="Editare/Validare Date Dimensiune"
      description="Vă rugăm să introduceți valorile corecte pentru ultimul exercițiu financiar încheiat."
      currentStep={currentStep}
      steps={steps}
      currentSubSteps={[
        {
          id: "3.A",
          label: "Confirmare Date Dimensiune Entitate",
          status: "completed",
        },
        {
          id: "3.B",
          label: "Validare Date Dimensiune Entitate",
          status: "current",
        },
        {
          id: "3.C",
          label: "Rezultat Evaluare",
          status: "upcoming",
        },
      ]}
      onBack={onBack}
      onStepClick={onStepClick}
      showBackButton
      userAvatar={userAvatar}
    >
      <div className="space-y-6">
        {sizeEvaluation && (
          <div className="flex items-center gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0" />
            <div>
              <p className="font-medium text-warning text-xs md:text-sm">
                Evaluare în timp real
              </p>
              <p className="text-xs md:text-sm text-warning/80">
                Dimensiunea actuală: <strong>{sizeEvaluation.size}</strong>
              </p>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-xl">
              Date Financiare și de Personal
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Toate valorile trebuie să se refere la ultimul exercițiu financiar
              încheiat
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 ppt-0 pb-6 md:pt-0 md:pb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="salariati" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Numărul mediu anual de salariați *
                </Label>
                <Input
                  id="salariati"
                  type="number"
                  value={financialData.nrSalariati}
                  onChange={(e) =>
                    handleDataChange(
                      "nrSalariati",
                      parseInt(e.target.value) || 0
                    )
                  }
                  className={
                    fieldErrors.nrSalariati
                      ? "text-lg h-12 border-destructive focus-visible:ring-destructive"
                      : "text-lg h-12"
                  }
                  aria-invalid={!!fieldErrors.nrSalariati}
                />
                {fieldErrors.nrSalariati ? (
                  <p className="text-xs md:text-sm text-destructive">
                    {fieldErrors.nrSalariati}
                  </p>
                ) : (
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Includeți toți angajații echivalent timp plin
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="cifraAfaceri"
                  className="flex items-center gap-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  Cifra de afaceri anuală netă (EUR) *
                </Label>
                <Input
                  id="cifraAfaceri"
                  type="number"
                  value={financialData.cifraAfaceri}
                  onChange={(e) =>
                    handleDataChange(
                      "cifraAfaceri",
                      parseInt(e.target.value) || 0
                    )
                  }
                  className={
                    fieldErrors.cifraAfaceri
                      ? "text-lg h-12 border-destructive focus-visible:ring-destructive"
                      : "text-lg h-12"
                  }
                  aria-invalid={!!fieldErrors.cifraAfaceri}
                />
                {fieldErrors.cifraAfaceri ? (
                  <p className="text-xs md:text-sm text-destructive">
                    {fieldErrors.cifraAfaceri}
                  </p>
                ) : (
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Introduceți valoarea în EUR.{" "}
                    {cifraAfaceriRon > 0
                      ? `Valoarea originală (RON): ${formatCurrency(cifraAfaceriRon, "RON")}`
                      : "Valoarea din contul de profit și pierdere"}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="active" className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Activele totale deținute (EUR) *
                </Label>
                <Input
                  id="active"
                  type="number"
                  value={financialData.active}
                  onChange={(e) =>
                    handleDataChange("active", parseInt(e.target.value) || 0)
                  }
                  className={
                    fieldErrors.active
                      ? "text-lg h-12 border-destructive focus-visible:ring-destructive"
                      : "text-lg h-12"
                  }
                  aria-invalid={!!fieldErrors.active}
                />
                {fieldErrors.active ? (
                  <p className="text-xs md:text-sm text-destructive">
                    {fieldErrors.active}
                  </p>
                ) : (
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Introduceți valoarea în EUR.{" "}
                    {activeRon > 0
                      ? `Valoarea originală (RON): ${formatCurrency(activeRon, "RON")}`
                      : "Valoarea din bilanțul contabil"}
                  </p>
                )}
              </div>
            </div>

            {sizeEvaluation && (
              <div className="p-3 md:p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={getSizeBadgeVariant(sizeEvaluation.size)}>
                    ENTITATE {sizeEvaluation.size}
                  </Badge>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {sizeEvaluation.explanation}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-3 justify-between pt-4 md:pt-6">
          <Button
            className="w-full md:w-auto text-xs md:text-sm"
            variant="outline"
            onClick={() => setIsEditMode(false)}
          >
            Înapoi
          </Button>

          <PaywallButton
            locked={!isLoadingCredits && hasInsufficientCredits}
            leftIcon={
              isLoadingCredits ? undefined : (
                <CheckCircle2 className="w-5 h-5" />
              )
            }
            onClick={handleContinue}
            size="lg"
            variant="hero"
            className="min-w-48 w-full md:w-auto text-xs md:text-sm"
            disabled={isLoadingCredits}
          >
            {isLoadingCredits ? (
              <div className="h-5 w-5 border-2 border-background border-t-transparent rounded-full animate-spin" />
            ) : (
              "Validez Datele și Continui"
            )}
          </PaywallButton>
        </div>
      </div>
    </WizardLayout>
  );
}
